import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { HomeLayout } from "../layout/HomeLayout";
import {
  BEGINNER_PATH_PHASES,
  getChapterForPhase,
  type BeginnerPathPhase,
} from "../../lib/beginnerPath/beginnerPathPlan";
import {
  BEGINNER_PATH_THEME as T,
  pathAccentEyebrow,
} from "../../lib/beginnerPath/beginnerPathTheme";
import { loadBeginnerPathProgress } from "../../lib/beginnerPath/beginnerPathPlan";
import {
  BEGINNER_PATH_PAGE_CLASS,
  BEGINNER_PATH_SECTION_CLASS,
} from "../../lib/beginnerPath/beginnerPathLayout";

export interface BeginnerPathPhaseShellProps {
  phase: BeginnerPathPhase;
  children: React.ReactNode;
  /** Contenido a ancho completo (ej. estudio de ejercicio con mástil). */
  fullBleed?: boolean;
}

export function BeginnerPathPhaseShell({
  phase,
  children,
  fullBleed = false,
}: BeginnerPathPhaseShellProps) {
  const navigate = useNavigate();
  const pathProgress = loadBeginnerPathProgress();
  const chapter = getChapterForPhase(phase.id);
  const chapterPhases = chapter?.phases ?? BEGINNER_PATH_PHASES;
  const completedCount = chapterPhases.filter((p) =>
    pathProgress.completedPhaseIds.includes(p.id)
  ).length;
  const total = chapterPhases.length;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;
  const phaseDone = pathProgress.completedPhaseIds.includes(phase.id);

  /** Estudio (ejercicio/misión): sin header de fase ni AppBar global. */
  if (fullBleed) {
    return (
      <HomeLayout hideAppBar>
        <div className="flex h-full min-h-0 w-full flex-1 flex-col pt-3 px-3 sm:pt-4 sm:px-4 pb-2">
          {children}
        </div>
      </HomeLayout>
    );
  }

  return (
    <HomeLayout>
      <div className={BEGINNER_PATH_PAGE_CLASS}>
        <button
          type="button"
          onClick={() => navigate("/learn/path")}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#99f6e4]"
          style={{ color: T.muted }}
        >
          <ArrowLeft className="h-4 w-4" />
          Mapa de caminos
        </button>

        <header className="mt-8 mb-6 lg:mb-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
            <div className="min-w-0 flex-1">
              <p
                className="text-xs tracking-widest uppercase"
                style={{ color: pathAccentEyebrow(phase.accent) }}
              >
                Paso {phase.order}
                {chapter ? ` · ${chapter.title}` : " · Camino principiante"}
              </p>
              <h1
                className="mt-3 font-serif font-light tracking-tight text-[clamp(1.75rem,4vw,3rem)] leading-[1.05]"
                style={{ color: T.text }}
              >
                {phase.title}
              </h1>
              <p className="mt-2 text-sm font-medium lg:text-base" style={{ color: T.textSoft }}>
                {phase.subtitle}
              </p>
              <p
                className="mt-2 max-w-3xl text-sm leading-relaxed lg:text-[15px]"
                style={{ color: T.textMuted }}
              >
                {phase.description}
              </p>
            </div>

            <div className="w-full shrink-0 lg:w-[min(100%,340px)]">
              <div
                className="flex items-center justify-between text-[10px] uppercase tracking-[0.12em] mb-1.5"
                style={{ color: T.textDim }}
              >
                <span>{chapter?.title ?? "Tu camino"}</span>
                <span>
                  {completedCount}/{total} fases · {pct}%
                </span>
              </div>
              <div
                className="h-1.5 rounded-full overflow-hidden"
                style={{ background: T.progressTrack }}
              >
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, background: T.progressFill }}
                />
              </div>
              {phaseDone && (
                <span
                  className="mt-3 inline-flex text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full"
                  style={{
                    color: "#99f6e4",
                    background: "rgba(153, 246, 228, 0.12)",
                    border: "1px solid rgba(153, 246, 228, 0.35)",
                  }}
                >
                  Fase completada
                </span>
              )}
            </div>
          </div>
        </header>

        <div className={BEGINNER_PATH_SECTION_CLASS}>{children}</div>
      </div>
    </HomeLayout>
  );
}

export default BeginnerPathPhaseShell;
